import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { toIsoLocal } from '../lib/dateTime';
import {
  Euro,
  Plus,
  Edit2,
  Trash2,
  Lock,
  AlertTriangle,
  Calculator,
  ChevronDown,
  ChevronUp,
  Clock,
  Calendar,
  History,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

/**
 * RateManager - Verwaltet Abrechnungssätze für Kunden, Projekte oder Tätigkeiten
 * 
 * Props:
 * - scope: 'customer' | 'project' | 'activity'
 * - customerId: Kunde-ID (für scope=customer oder project)
 * - projectId: Projekt-ID (für scope=project oder activity)
 * - activityType: Tätigkeitsart (für scope=activity)
 * - title: Optionaler Titel
 * - showHistory: Zeigt auch gelöschte Sätze
 */
export default function RateManager({
  scope,
  customerId,
  projectId,
  activityType,
  title,
  showHistory = false,
}) {
  const { api } = useAuth();
  const [rates, setRates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(showHistory);
  
  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    valid_from: '',
    valid_to: '',
    rate_type: 'hourly',
    note: '',
    close_previous: false,
  });
  
  // Rechenhilfe state
  const [calcMethod, setCalcMethod] = useState('percent');
  const [calcValue, setCalcValue] = useState('');
  const [calcExplanation, setCalcExplanation] = useState('');
  const [baseRateId, setBaseRateId] = useState(null);

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'bis auf Weiteres';
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  // Fetch rates
  const fetchRates = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        scope: scope,
        include_expired: 'true',
      });
      if (customerId) params.append('customer_id', customerId);
      if (projectId) params.append('project_id', projectId);
      
      const response = await api.get(`/rates?${params.toString()}`);
      
      // Filter by activity_type if needed
      let filteredRates = response.data;
      if (scope === 'activity' && activityType) {
        filteredRates = response.data.filter(r => r.activity_type === activityType);
      } else if (scope !== 'activity') {
        filteredRates = response.data.filter(r => r.scope === scope);
      }
      
      // Sort chronologically (newest first)
      filteredRates.sort((a, b) => b.valid_from.localeCompare(a.valid_from));
      
      setRates(filteredRates);
    } catch (error) {
      console.error('Fehler beim Laden der Sätze:', error);
      toast.error('Fehler beim Laden der Sätze');
    } finally {
      setIsLoading(false);
    }
  }, [api, scope, customerId, projectId, activityType]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Check if rate is currently valid
  const isCurrentlyValid = (rate) => {
     const today = toIsoLocal(new Date());
    return rate.valid_from <= today && (!rate.valid_to || rate.valid_to >= today);
  };

  // Check for gaps in timeline
  const findGaps = () => {
    const activeRates = rates.filter(r => !r.deleted_at);
    if (activeRates.length <= 1) return [];
    
    const gaps = [];
    const sortedRates = [...activeRates].sort((a, b) => a.valid_from.localeCompare(b.valid_from));
    
    for (let i = 0; i < sortedRates.length - 1; i++) {
      const current = sortedRates[i];
      const next = sortedRates[i + 1];
      
      if (current.valid_to && current.rate_type === next.rate_type) {
        const gapStart = new Date(current.valid_to);
        gapStart.setDate(gapStart.getDate() + 1);
        const gapEnd = new Date(next.valid_from);
        gapEnd.setDate(gapEnd.getDate() - 1);
        
        if (gapStart <= gapEnd) {
          gaps.push({
            start: gapStart.toISOString().split('T')[0],
            end: gapEnd.toISOString().split('T')[0],
            rate_type: current.rate_type,
          });
        }
      }
    }
    return gaps;
  };

  // Open create dialog
  const openCreateDialog = () => {
    setEditingRate(null);
    setFormData({
      amount: '',
      valid_from: toIsoLocal(new Date()),
      valid_to: '',
      rate_type: 'hourly',
      note: '',
      close_previous: false,
    });
    setCalcExplanation('');
    setCalcValue('');
    
    // Set base rate for calculation
    const activeRates = rates.filter(r => !r.deleted_at);
    if (activeRates.length > 0) {
      setBaseRateId(activeRates[0].id);
    } else {
      setBaseRateId(null);
    }
    
    setIsDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (rate) => {
    setEditingRate(rate);
    setFormData({
      amount: rate.amount.toString().replace('.', ','),
      valid_from: rate.valid_from,
      valid_to: rate.valid_to || '',
      rate_type: rate.rate_type,
      note: rate.note || '',
      close_previous: false,
    });
    setCalcExplanation('');
    setCalcValue('');
    setIsDialogOpen(true);
  };

  // Calculate suggested rate
  const calculateSuggestion = async () => {
    if (!calcValue || !baseRateId) {
      toast.error('Bitte wählen Sie einen Basissatz und geben Sie einen Wert ein');
      return;
    }
    
    try {
      const response = await api.post('/rates/suggest', {
        base_rate_id: baseRateId,
        method: calcMethod,
        value: parseFloat(calcValue.replace(',', '.')),
      });
      
      setFormData(prev => ({
        ...prev,
        amount: response.data.suggested_amount.toString().replace('.', ','),
      }));
      setCalcExplanation(response.data.explanation);
    } catch (error) {
      console.error('Fehler bei der Berechnung:', error);
      toast.error('Fehler bei der Satzberechnung');
    }
  };

  // Save rate
  const handleSave = async () => {
    const amount = parseFloat(formData.amount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      toast.error('Bitte geben Sie einen gültigen Betrag ein');
      return;
    }
    
    if (!formData.valid_from) {
      toast.error('Bitte geben Sie ein Startdatum ein');
      return;
    }
    
    try {
      const payload = {
        scope: scope,
        customer_id: customerId || undefined,
        project_id: projectId || undefined,
        activity_type: activityType || undefined,
        rate_type: formData.rate_type,
        amount: amount,
        valid_from: formData.valid_from,
        valid_to: formData.valid_to || null,
        note: formData.note || null,
        close_previous: formData.close_previous,
      };
      
      if (editingRate) {
        await api.put(`/rates/${editingRate.id}`, {
          amount: amount,
          valid_to: formData.valid_to || null,
          note: formData.note || null,
        });
        toast.success('Satz aktualisiert');
      } else {
        await api.post('/rates', payload);
        toast.success('Satz erstellt');
      }
      
      setIsDialogOpen(false);
      fetchRates();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        if (typeof detail === 'object' && detail.message) {
          toast.error(detail.message);
          if (detail.hint) {
            toast.info(detail.hint);
          }
        } else {
          toast.error('Überlappung mit bestehendem Satz');
        }
      } else {
        toast.error('Fehler beim Speichern des Satzes');
      }
    }
  };

  // Delete rate
  const handleDelete = async (rate) => {
    const reason = window.prompt('Bitte geben Sie einen Löschgrund ein (min. 5 Zeichen):');
    if (!reason || reason.length < 5) {
      toast.error('Löschgrund muss mindestens 5 Zeichen haben');
      return;
    }
    
    try {
      await api.delete(`/rates/${rate.id}?reason=${encodeURIComponent(reason)}`);
      toast.success('Satz gelöscht');
      fetchRates();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      if (error.response?.status === 409) {
        toast.error('Satz kann nicht gelöscht werden - bereits in Abrechnung verwendet');
      } else {
        toast.error('Fehler beim Löschen des Satzes');
      }
    }
  };

  const gaps = findGaps();
  const activeRates = rates.filter(r => !r.deleted_at);
  const deletedRates = rates.filter(r => r.deleted_at);

  const getScopeLabel = () => {
    switch (scope) {
      case 'customer': return 'Kundensatz';
      case 'project': return 'Projektsatz';
      case 'activity': return `Tätigkeitssatz (${activityType})`;
      default: return 'Satz';
    }
  };

  return (
    <div className="space-y-4" data-testid="rate-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Euro className="h-4 w-4" />
          {title || getScopeLabel()}
        </h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openCreateDialog}
          data-testid="rate-create-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Neuer Satz
        </Button>
      </div>

      {/* Gaps warning */}
      {gaps.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200" data-testid="rate-gaps-warning">
          <div className="flex items-center gap-2 font-medium mb-1">
            <AlertTriangle className="h-4 w-4" />
            Lücken im Satzverlauf
          </div>
          <ul className="list-disc list-inside text-amber-700 dark:text-amber-300/80">
            {gaps.map((gap, i) => (
              <li key={i}>
                {formatDate(gap.start)} – {formatDate(gap.end)} ({gap.rate_type === 'hourly' ? 'Stundensatz' : 'Tagessatz'})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Priority info */}
      <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
        <span className="font-medium">Vorrangregel:</span> Tätigkeitssatz → Projektsatz → Kundensatz.
        Der speziellste Satz zum Leistungsdatum wird verwendet.
      </div>

      {/* Rates list */}
      {isLoading ? (
        <div className="text-center text-muted-foreground py-4">Lädt...</div>
      ) : activeRates.length === 0 ? (
        <div className="text-center text-muted-foreground py-4 border border-dashed border-border rounded-lg" data-testid="rate-empty">
          Keine Sätze hinterlegt
        </div>
      ) : (
        <div className="space-y-2" data-testid="rate-list">
          {activeRates.map((rate) => {
            const isCurrent = isCurrentlyValid(rate);
            const isLocked = rate.is_used_in_billing;
            
            return (
              <div
                key={rate.id}
                className={`rounded-lg border p-3 transition-colors ${
                  isCurrent
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-muted/50 border-border'
                }`}
                data-testid={`rate-item-${rate.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-foreground">
                        {formatCurrency(rate.amount)}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                        {rate.rate_type === 'hourly' ? '/Stunde' : '/Tag'}
                      </span>
                      {isCurrent && (
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
                          aktuell
                        </span>
                      )}
                      {isLocked && (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1" title="In Abrechnung verwendet">
                          <Lock className="h-3 w-3" />
                          historisch
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(rate.valid_from)} – {formatDate(rate.valid_to)}
                    </div>
                    {rate.note && (
                      <div className="text-xs text-muted-foreground mt-1">{rate.note}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(rate)}
                      disabled={isLocked}
                      title={isLocked ? 'Historisch gebunden, nicht änderbar' : 'Bearbeiten'}
                      data-testid={`rate-edit-${rate.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(rate)}
                      disabled={isLocked}
                      title={isLocked ? 'Historisch gebunden, nicht löschbar' : 'Löschen'}
                      className="text-red-600 dark:text-red-400 hover:text-red-300"
                      data-testid={`rate-delete-${rate.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* History collapsible */}
      {deletedRates.length > 0 && (
        <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="w-full justify-between text-muted-foreground" data-testid="rate-history-toggle">
              <span className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Satzverlauf ({deletedRates.length} gelöscht)
              </span>
              {historyOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 mt-2">
            {deletedRates.map((rate) => (
              <div
                key={rate.id}
                className="rounded-lg border border-border bg-card/50 p-3 opacity-60"
                data-testid={`rate-deleted-${rate.id}`}
              >
                <div className="flex items-center gap-2 line-through text-muted-foreground">
                  <span>{formatCurrency(rate.amount)}</span>
                  <span className="text-xs">
                    {rate.rate_type === 'hourly' ? '/Stunde' : '/Tag'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(rate.valid_from)} – {formatDate(rate.valid_to)}
                </div>
                {rate.delete_reason && (
                  <div className="text-xs text-red-600 dark:text-red-400/60 mt-1">
                    Gelöscht: {rate.delete_reason}
                  </div>
                )}
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="rate-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingRate ? 'Satz bearbeiten' : 'Neuen Satz anlegen'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {getScopeLabel()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Amount */}
            <div>
              <Label>Betrag (€)</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0,00"
                  className="bg-muted border-border"
                  data-testid="rate-amount-input"
                />
                <Select
                  value={formData.rate_type}
                  onValueChange={(v) => setFormData({ ...formData, rate_type: v })}
                  disabled={!!editingRate}
                >
                  <SelectTrigger className="w-32 bg-muted border-border" data-testid="rate-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">/Stunde</SelectItem>
                    <SelectItem value="daily">/Tag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Rechenhilfe (only for new rates) */}
            {!editingRate && activeRates.length > 0 && (
              <div className="bg-muted/50 border border-border rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Calculator className="h-4 w-4" />
                  Rechenhilfe
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={calcMethod} onValueChange={setCalcMethod}>
                    <SelectTrigger className="bg-muted border-border" data-testid="calc-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Prozentuale Erhöhung</SelectItem>
                      <SelectItem value="inflation">Inflationswert</SelectItem>
                      <SelectItem value="absolute">Absoluter Betrag</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="text"
                    value={calcValue}
                    onChange={(e) => setCalcValue(e.target.value)}
                    placeholder={calcMethod === 'absolute' ? '€' : '%'}
                    className="bg-muted border-border"
                    data-testid="calc-value-input"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={calculateSuggestion}
                  className="w-full"
                  data-testid="calc-btn"
                >
                  Berechnen
                </Button>
                {calcExplanation && (
                  <div className="text-sm text-emerald-600 dark:text-emerald-400" data-testid="calc-explanation">
                    {calcExplanation}
                  </div>
                )}
              </div>
            )}
            
            {/* Valid from/to */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gültig ab</Label>
                <Input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  className="bg-muted border-border"
                  disabled={!!editingRate}
                  data-testid="rate-valid-from-input"
                />
              </div>
              <div>
                <Label>Gültig bis (optional)</Label>
                <Input
                  type="date"
                  value={formData.valid_to}
                  onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                  className="bg-muted border-border"
                  data-testid="rate-valid-to-input"
                />
              </div>
            </div>
            
            {/* Close previous */}
            {!editingRate && (
              <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                <Label className="text-sm text-muted-foreground">
                  Vorherigen offenen Satz automatisch zum Vortag beenden
                </Label>
                <Switch
                  checked={formData.close_previous}
                  onCheckedChange={(v) => setFormData({ ...formData, close_previous: v })}
                  data-testid="rate-close-previous-switch"
                />
              </div>
            )}
            
            {/* Note */}
            <div>
              <Label>Notiz (optional)</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="z.B. Inflationsanpassung 2026"
                className="bg-muted border-border min-h-[60px]"
                data-testid="rate-note-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleSave} data-testid="rate-save-btn">
              {editingRate ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
